import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../config';
import ClientItinerary from './ClientItinerary';
import ProposalViewer from './ProposalViewer';

const ClientIndex = () => {
  const [trips, setTrips] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (user && user.role === 'client') {
      fetchClientData();
    }
  }, [user]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      
      // Fetch itineraries for the logged-in client
      const tripsResponse = await axios.get(`${API_BASE_URL}/api/trips`);
      
      // Get client data to get the actual name
      let clientName = null;
      try {
        const clientResponse = await axios.get(`${API_BASE_URL}/api/clients/${user.clientId}`);
        const clientData = clientResponse.data.data;
        clientName = `${clientData.name} ${clientData.lastName1}`.trim();
      } catch (error) {
        console.error('Error fetching client data:', error);
      }
      
      // Filter itineraries by client name
      const clientItineraries = tripsResponse.data.data.filter(trip => {
        // Normalize both names by replacing multiple spaces with single space and trimming
        const normalizedTripTraveler = trip.traveler.replace(/\s+/g, ' ').trim();
        const normalizedClientName = clientName ? clientName.replace(/\s+/g, ' ').trim() : '';
        
        // Try by normalized client name from database
        if (normalizedClientName && normalizedTripTraveler === normalizedClientName) {
          return true;
        }
        // Try by client name from database (original)
        if (clientName && trip.traveler === clientName) {
          return true;
        }
        // Try by email if traveler matches email
        if (trip.traveler === user.email) {
          return true;
        }
        // Try by name if available
        if (user.name && user.lastName1 && trip.traveler === `${user.name} ${user.lastName1}`) {
          return true;
        }
        return false;
      });
      setTrips(clientItineraries);

      // Fetch proposals for the logged-in client
      const proposalsResponse = await axios.get(`${API_BASE_URL}/api/proposals`);
      const clientProposals = proposalsResponse.data.data.filter(proposal => 
        proposal.clientId && proposal.clientId._id === user.clientId
      );
      setProposals(clientProposals);

      // Determine what content to show
      determineContent(clientItineraries, clientProposals);
      
    } catch (error) {
      console.error('Error fetching client data:', error);
      setContent('error');
    } finally {
      setLoading(false);
    }
  };

  const determineContent = (clientItineraries, clientProposals) => {
    // Check if any itinerary has active "Show Itinerary" status
    const hasActiveItinerary = clientItineraries.some(trip => trip.showItinerary === true);
    
    if (hasActiveItinerary) {
      // Render ClientItinerary - ignore proposal statuses
      setContent('itinerary');
      return;
    }

    // Check if client has any "Published" proposals
    const hasPublishedProposal = clientProposals.some(proposal => proposal.status === 'published');
    
    if (hasPublishedProposal) {
      // Render ProposalViewer
      setContent('proposal');
      return;
    }

    // No active itinerary and no published proposals
    setContent('nothing');
  };

  const renderContent = () => {
    switch (content) {
      case 'itinerary':
        return <ClientItinerary />;
      case 'proposal':
        // Find the first published proposal to display
        const publishedProposal = proposals.find(proposal => proposal.status === 'published');
        return publishedProposal ? <ProposalViewer proposal={publishedProposal} /> : <div>No published proposal found</div>;
      case 'nothing':
        return (
          <div style={{ 
            textAlign: 'center', 
            padding: '50px', 
            fontSize: '1.2em',
            color: '#666'
          }}>
            Nothing to see. <br></br>
            If you should have something to see, please contact Megan
          </div>
        );
      case 'error':
        return (
          <div style={{ 
            textAlign: 'center', 
            padding: '50px', 
            fontSize: '1.2em',
            color: '#dc3545'
          }}>
            Error loading content. Please try again.
          </div>
        );
      default:
        return (
          <div style={{ 
            textAlign: 'center', 
            padding: '50px', 
            fontSize: '1.2em'
          }}>
            Loading...
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px', 
        fontSize: '1.2em'
      }}>
        Loading your dashboard...
      </div>
    );
  }

  return (
    <div className="App">
      {/* Debug Information */}
      {/* <div style={{ 
        background: '#f8f9fa', 
        border: '1px solid #dee2e6', 
        borderRadius: '4px', 
        padding: '10px', 
        margin: '10px 0',
        fontSize: '12px',
        fontFamily: 'monospace'
      }}>
        <strong>Debug Info:</strong><br/>
        <strong>Client Itineraries ({trips.length}):</strong> {trips.map(trip => `${trip.title} (${trip.showItinerary ? 'Active' : 'Inactive'})`).join(', ')}<br/>
        <strong>Client Proposals ({proposals.length}):</strong> {proposals.map(proposal => `${proposal.title} (${proposal.status})`).join(', ')}<br/>
        <strong>Content Type:</strong> {content}
      </div> */}
      
      {renderContent()}
    </div>
  );
};

export default ClientIndex;
